import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ThumbsUp, MessageCircle, Share2, MoreHorizontal, Trash2,
  CreditCard as EditIcon, Send, X, Users, Globe, User,
  UserPlus, UserCheck, UserX, Bell, BellOff, Loader2,
  Link as LinkIcon, Check, Move, Upload
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

interface PostProps {
  post: any;
  onDelete?: () => void;
  onUpdate?: () => void;
  theme: 'light' | 'dark';
  readOnly?: boolean;
  onReadOnlyAction?: () => void;
}

type ConnectionStatus = 'none' | 'pending_sent' | 'pending_received' | 'accepted' | 'declined';

export default function CommunityPost({ post, onDelete, onUpdate, theme, readOnly = false, onReadOnlyAction }: PostProps) {
  const { user } = useAuth();
  const [consultant, setConsultant] = useState<any>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [sharesCount, setSharesCount] = useState(post.shares_count || 0);

  useEffect(() => {
    setLikesCount(post.likes_count || 0);
  }, [post.likes_count]);

  useEffect(() => {
    setCommentsCount(post.comments_count || 0);
  }, [post.comments_count]);

  useEffect(() => {
    setSharesCount(post.shares_count || 0);
  }, [post.shares_count]);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(post.content);
  const [editedVisibility, setEditedVisibility] = useState<'community' | 'public'>(post.visibility || 'community');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [currentUserPhotoUrl, setCurrentUserPhotoUrl] = useState<string | null>(null);
  const [adminProfilePhotoUrl, setAdminProfilePhotoUrl] = useState<string | null>(post.admin_photo_url || null);
  const [adminDisplayName, setAdminDisplayName] = useState<string | null>(null);
  const [photoPosition, setPhotoPosition] = useState({ x: 50, y: 50 });

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('none');
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showConnectionTooltip, setShowConnectionTooltip] = useState(false);
  const [showFollowTooltip, setShowFollowTooltip] = useState(false);
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [followersCount, setFollowersCount] = useState(0);

  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareConsultants, setShareConsultants] = useState<any[]>([]);
  const [shareSearch, setShareSearch] = useState('');
  const [sharedTo, setSharedTo] = useState<Set<string>>(new Set());
  const [linkCopied, setLinkCopied] = useState(false);
  const shareMenuRef = useRef<HTMLDivElement>(null);

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [isSavingCommentEdit, setIsSavingCommentEdit] = useState(false);

  const initPositions = () => {
    const saved = Array.isArray(post.media_positions) ? post.media_positions : [];
    const urls = Array.isArray(post.media_urls) ? post.media_urls : [];
    return urls.map((_: string, i: number) => ({
      x: saved[i]?.x ?? 50,
      y: saved[i]?.y ?? 50,
    }));
  };
  const [mediaPositions, setMediaPositions] = useState<{ x: number; y: number }[]>(initPositions);
  const [adjustingIndex, setAdjustingIndex] = useState<number | null>(null);
  const [pendingNewMedia, setPendingNewMedia] = useState<{ file: File; preview: string } | null>(null);
  const editImageInputRef = useRef<HTMLInputElement>(null);
  const mediaDragRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isDraggingMedia = useRef(false);
  const mediaDragStart = useRef({ x: 0, y: 0, posX: 50, posY: 50, idx: 0 });

  const isAdminPost = post.is_admin_post === true;
  const isOwnPost = user?.id === post.consultant_id;

  useEffect(() => {
    if (!isAdminPost) {
      fetchConsultantDetails();
    } else {
      fetchAdminPhoto();
    }
    checkIfLiked();
    fetchCurrentUserPhoto();
    fetchLikeCount();
    fetchCommentCount();
    if (user && !isOwnPost && !isAdminPost) {
      checkConnectionStatus();
      checkFollowStatus();
    }
    if (!isAdminPost) {
      fetchConnectionsCount();
      fetchFollowersCount();
    }
  }, [post.id, user?.id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  const fetchAdminPhoto = async () => {
    try {
      const { data } = await supabase
        .from('admin_profiles')
        .select('photo_url, display_name, first_name, last_name')
        .eq('id', post.consultant_id)
        .maybeSingle();
      if (data) {
        if (data.photo_url) setAdminProfilePhotoUrl(data.photo_url);
        const name = data.display_name || [data.first_name, data.last_name].filter(Boolean).join(' ');
        if (name) setAdminDisplayName(name);
        return;
      }
      // fallback: use admin_photo_url stored on the post
      if (post.admin_photo_url) setAdminProfilePhotoUrl(post.admin_photo_url);
    } catch (error) {
      console.error('Error fetching admin photo:', error);
    }
  };

  const fetchCurrentUserPhoto = async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        .from('consultant_profiles')
        .select('photo_url')
        .eq('id', user.id)
        .maybeSingle();
      if (data?.photo_url) setCurrentUserPhotoUrl(getPhotoUrl(data.photo_url));
    } catch (error) {
      console.error('Error fetching current user photo:', error);
    }
  };

  const fetchConsultantDetails = async () => {
    try {
      const { data } = await supabase
        .from('consultant_profiles')
        .select('first_name, last_name, email, certifications, experience_years, photo_url, company, photo_position_x, photo_position_y')
        .eq('id', post.consultant_id)
        .maybeSingle();

      if (data) {
        setConsultant({
          ...data,
          full_name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
          workday_certifications: data.certifications ? [data.certifications] : []
        });
        setPhotoPosition({
          x: data.photo_position_x ?? 50,
          y: data.photo_position_y ?? 50
        });
      }
    } catch (error) {
      console.error('Error fetching consultant:', error);
    }
  };

  const getPhotoUrl = (photoPath: string | null) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
      return photoPath;
    }
    const { data } = supabase.storage.from('profile-photos').getPublicUrl(photoPath);
    return data.publicUrl;
  };

  const checkIfLiked = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('consultant_post_likes')
        .select('id')
        .eq('post_id', post.id)
        .eq('consultant_id', user.id)
        .maybeSingle();
      setIsLiked(!!data);
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  const checkConnectionStatus = async () => {
    if (!user?.id || !post.consultant_id) return;
    try {
      const { data } = await supabase
        .from('community_connections')
        .select('id, status, requester_id, addressee_id')
        .or(
          `and(requester_id.eq.${user.id},addressee_id.eq.${post.consultant_id}),and(requester_id.eq.${post.consultant_id},addressee_id.eq.${user.id})`
        )
        .maybeSingle();

      if (!data) {
        setConnectionStatus('none');
        setConnectionId(null);
        return;
      }

      setConnectionId(data.id);
      if (data.status === 'accepted') {
        setConnectionStatus('accepted');
      } else if (data.status === 'declined') {
        setConnectionStatus('declined');
      } else if (data.requester_id === user.id) {
        setConnectionStatus('pending_sent');
      } else {
        setConnectionStatus('pending_received');
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
    }
  };

  const checkFollowStatus = async () => {
    if (!user?.id || !post.consultant_id) return;
    try {
      const { data } = await supabase
        .from('community_followers')
        .select('id')
        .eq('follower_id', user.id)
        .eq('following_id', post.consultant_id)
        .maybeSingle();
      setIsFollowing(!!data);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const fetchConnectionsCount = async () => {
    if (!post.consultant_id) return;
    try {
      const { count } = await supabase
        .from('community_connections')
        .select('id', { count: 'exact', head: true })
        .or(`requester_id.eq.${post.consultant_id},addressee_id.eq.${post.consultant_id}`)
        .eq('status', 'accepted');
      if (count !== null) setConnectionsCount(count);
    } catch (error) {
      console.error('Error fetching connections count:', error);
    }
  };

  const fetchFollowersCount = async () => {
    if (!post.consultant_id) return;
    try {
      const { count } = await supabase
        .from('community_followers')
        .select('id', { count: 'exact', head: true })
        .eq('following_id', post.consultant_id);
      if (count !== null) setFollowersCount(count);
    } catch (error) {
      console.error('Error fetching followers count:', error);
    }
  };

  const handleConnect = async () => {
    if (!user || connectionLoading) return;
    setConnectionLoading(true);
    try {
      if (connectionStatus === 'none' || connectionStatus === 'declined') {
        const { data, error } = await supabase
          .from('community_connections')
          .insert({ requester_id: user.id, addressee_id: post.consultant_id })
          .select('id')
          .single();
        if (error) throw error;
        setConnectionId(data.id);
        setConnectionStatus('pending_sent');
      } else if (connectionStatus === 'pending_sent' && connectionId) {
        const { error } = await supabase
          .from('community_connections')
          .delete()
          .eq('id', connectionId);
        if (error) throw error;
        setConnectionStatus('none');
        setConnectionId(null);
      } else if (connectionStatus === 'pending_received' && connectionId) {
        const { error } = await supabase
          .from('community_connections')
          .update({ status: 'accepted', updated_at: new Date().toISOString() })
          .eq('id', connectionId);
        if (error) throw error;
        setConnectionStatus('accepted');
      } else if (connectionStatus === 'accepted' && connectionId) {
        const { error } = await supabase
          .from('community_connections')
          .delete()
          .eq('id', connectionId);
        if (error) throw error;
        setConnectionStatus('none');
        setConnectionId(null);
      }
    } catch (error) {
      console.error('Error updating connection:', error);
    } finally {
      setConnectionLoading(false);
      fetchConnectionsCount();
    }
  };

  const handleFollow = async () => {
    if (!user || followLoading) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await supabase
          .from('community_followers')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', post.consultant_id);
        setIsFollowing(false);
      } else {
        await supabase
          .from('community_followers')
          .insert({ follower_id: user.id, following_id: post.consultant_id });
        setIsFollowing(true);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoading(false);
      fetchFollowersCount();
    }
  };

  const handleLike = async () => {
    if (!user || readOnly) return;
    try {
      if (isLiked) {
        await supabase.from('consultant_post_likes').delete()
          .eq('post_id', post.id).eq('consultant_id', user.id);
        setIsLiked(false);
      } else {
        await supabase.from('consultant_post_likes').insert({ post_id: post.id, consultant_id: user.id });
        setIsLiked(true);
      }
      await fetchLikeCount();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const fetchLikeCount = async () => {
    try {
      const { count } = await supabase
        .from('consultant_post_likes')
        .select('id', { count: 'exact', head: true })
        .eq('post_id', post.id);
      if (count !== null) setLikesCount(count);
    } catch (error) {
      console.error('Error fetching like count:', error);
    }
  };

  const fetchCommentCount = async () => {
    try {
      const { count } = await supabase
        .from('consultant_post_comments')
        .select('id', { count: 'exact', head: true })
        .eq('post_id', post.id)
        .is('parent_comment_id', null);
      if (count !== null) setCommentsCount(count);
    } catch (error) {
      console.error('Error fetching comment count:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const { data } = await supabase
        .from('consultant_post_comments')
        .select('*, consultant:consultant_profiles(first_name, last_name, email, photo_url)')
        .eq('post_id', post.id)
        .is('parent_comment_id', null)
        .order('created_at', { ascending: true });

      const mapped = (data || []).map(comment => ({
        ...comment,
        consultant: comment.consultant ? {
          ...comment.consultant,
          full_name: `${comment.consultant.first_name || ''} ${comment.consultant.last_name || ''}`.trim()
        } : null
      }));
      setComments(mapped);
      setCommentsCount(mapped.length);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;
    setIsSubmittingComment(true);
    try {
      await supabase.from('consultant_post_comments').insert({
        post_id: post.id, consultant_id: user.id, content: newComment.trim()
      });
      setNewComment('');
      await fetchComments();
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleEditComment = (comment: any) => {
    setEditingCommentId(comment.id);
    setEditingCommentContent(comment.content);
  };

  const handleSaveCommentEdit = async (commentId: string) => {
    if (!editingCommentContent.trim()) return;
    setIsSavingCommentEdit(true);
    try {
      await supabase
        .from('consultant_post_comments')
        .update({ content: editingCommentContent.trim(), updated_at: new Date().toISOString() })
        .eq('id', commentId);
      setEditingCommentId(null);
      await fetchComments();
    } catch (error) {
      console.error('Error editing comment:', error);
    } finally {
      setIsSavingCommentEdit(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Delete this comment?')) return;
    try {
      await supabase.from('consultant_post_comments').delete().eq('id', commentId);
      await fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const toggleComments = () => {
    if (!showComments) fetchComments();
    setShowComments(!showComments);
  };

  const getPostLink = () => {
    return `${window.location.origin}/community#post-${post.id}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getPostLink());
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = getPostLink();
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
    setShowShareMenu(false);
  };

  const fetchShareConsultants = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('consultant_profiles')
        .select('id, first_name, last_name, photo_url, company')
        .neq('id', user.id)
        .eq('account_status', 'active')
        .order('first_name', { ascending: true })
        .limit(50);
      setShareConsultants(data || []);
    } catch (error) {
      console.error('Error fetching consultants for share:', error);
    }
  };

  const handleOpenShareMenu = () => {
    if (readOnly) {
      handleCopyLink();
      return;
    }
    setShowShareMenu(!showShareMenu);
    if (!showShareMenu) {
      fetchShareConsultants();
    }
  };

  const handleShareToConsultant = async (targetUserId: string) => {
    if (!user || sharedTo.has(targetUserId)) return;
    try {
      await supabase.from('consultant_post_shares').insert({
        post_id: post.id,
        consultant_id: user.id
      }).select().maybeSingle();

      await supabase.from('notifications').insert({
        user_id: targetUserId,
        title: 'Post Shared With You',
        message: `A consultant shared a community post with you.`,
        type: 'info',
        category: 'community',
        link: `/consultant/community#post-${post.id}`,
        metadata: { post_id: post.id, shared_by: user.id }
      });

      setSharedTo(prev => new Set(prev).add(targetUserId));
      setSharesCount((prev: number) => prev + 1);
    } catch (error) {
      console.error('Error sharing to consultant:', error);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await supabase.from('consultant_community_posts').delete().eq('id', post.id);
      if (onDelete) onDelete();
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleMediaDragStart = useCallback((e: React.MouseEvent | React.TouchEvent, idx: number) => {
    e.preventDefault();
    isDraggingMedia.current = true;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const pos = mediaPositions[idx] ?? { x: 50, y: 50 };
    mediaDragStart.current = { x: clientX, y: clientY, posX: pos.x, posY: pos.y, idx };

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if (!isDraggingMedia.current) return;
      const el = mediaDragRefs.current[idx];
      if (!el) return;
      const cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
      const rect = el.getBoundingClientRect();
      const dx = cx - mediaDragStart.current.x;
      const dy = cy - mediaDragStart.current.y;
      const newX = Math.min(100, Math.max(0, mediaDragStart.current.posX - (dx / rect.width) * 100));
      const newY = Math.min(100, Math.max(0, mediaDragStart.current.posY - (dy / rect.height) * 100));
      setMediaPositions(prev => {
        const next = [...prev];
        next[idx] = { x: Math.round(newX), y: Math.round(newY) };
        return next;
      });
    };
    const onUp = () => {
      isDraggingMedia.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [mediaPositions]);

  const handleEditNewMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setPendingNewMedia({ file, preview });
    e.target.value = '';
  };

  const handleEditSave = async () => {
    if (!editedContent.trim()) return;
    setIsSavingEdit(true);
    try {
      const updatePayload: any = {
        content: editedContent.trim(),
        visibility: editedVisibility,
        media_positions: mediaPositions,
      };

      if (pendingNewMedia) {
        const fileExt = pendingNewMedia.file.name.split('.').pop();
        const fileName = `${post.consultant_id}/${Date.now()}.${fileExt}`;
        const { error: upErr } = await supabase.storage
          .from('community-media')
          .upload(fileName, pendingNewMedia.file);
        if (!upErr) {
          const { data: { publicUrl } } = supabase.storage
            .from('community-media')
            .getPublicUrl(fileName);
          const existingUrls = Array.isArray(post.media_urls) ? post.media_urls : [];
          updatePayload.media_urls = existingUrls.length > 0 ? [publicUrl] : [publicUrl];
          updatePayload.media_type = 'image';
          post.media_urls = updatePayload.media_urls;
          post.media_type = 'image';
          const newPos = mediaPositions.length > 0 ? [mediaPositions[0]] : [{ x: 50, y: 50 }];
          updatePayload.media_positions = newPos;
          setMediaPositions(newPos);
        }
        URL.revokeObjectURL(pendingNewMedia.preview);
        setPendingNewMedia(null);
      }

      await supabase.from('consultant_community_posts')
        .update(updatePayload)
        .eq('id', post.id);
      post.content = editedContent.trim();
      post.visibility = editedVisibility;
      post.media_positions = updatePayload.media_positions;
      setIsEditing(false);
      setAdjustingIndex(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating post:', error);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleEditCancel = () => {
    setEditedContent(post.content);
    setEditedVisibility(post.visibility || 'community');
    setMediaPositions(initPositions());
    setAdjustingIndex(null);
    setPendingNewMedia(null);
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    if (diffInHours < 1) return `${Math.floor(diffInMs / (1000 * 60))}m ago`;
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  const getConnectionLabel = () => {
    switch (connectionStatus) {
      case 'accepted': return 'Connected';
      case 'pending_sent': return 'Pending';
      case 'pending_received': return 'Accept';
      default: return 'Connect';
    }
  };

  const getConnectionIcon = () => {
    if (connectionLoading) return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    switch (connectionStatus) {
      case 'accepted': return <UserCheck className="w-3.5 h-3.5" />;
      case 'pending_sent': return <UserX className="w-3.5 h-3.5" />;
      case 'pending_received': return <UserCheck className="w-3.5 h-3.5" />;
      default: return <UserPlus className="w-3.5 h-3.5" />;
    }
  };

  const getConnectionTooltip = () => {
    switch (connectionStatus) {
      case 'accepted': return 'Remove connection';
      case 'pending_sent': return 'Cancel request';
      case 'pending_received': return 'Accept connection request';
      default: return 'Send connection request to unlock messaging and private interactions';
    }
  };

  const filteredShareConsultants = shareConsultants.filter(c => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    return name.includes(shareSearch.toLowerCase());
  });

  const mediaUrls = Array.isArray(post.media_urls) ? post.media_urls : [];

  const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const borderColor = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textMuted = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const hoverBg = theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100';

  const displayName = isAdminPost ? (adminDisplayName || 'Perplexity Team') : (consultant?.full_name || 'Loading...');
  const displayCompany = isAdminPost ? 'Company' : (consultant?.company || 'Company');
  const adminPhotoUrl = adminProfilePhotoUrl || post.admin_photo_url || null;

  return (
    <div className={`rounded-xl shadow-lg p-6 mb-4 ${cardBg}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {isAdminPost ? (
            <>
              {adminPhotoUrl && (
                <img
                  src={adminPhotoUrl}
                  alt="Perplexity Team"
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#40E0D0] flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fb = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fb) fb.style.display = 'flex';
                  }}
                />
              )}
              <div
                className="w-12 h-12 rounded-full bg-[#40E0D0] flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                style={{ display: adminPhotoUrl ? 'none' : 'flex' }}
              >
                {adminDisplayName ? adminDisplayName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() : 'PT'}
              </div>
            </>
          ) : (
            <>
              {consultant?.photo_url && getPhotoUrl(consultant.photo_url) && (
                <img
                  src={getPhotoUrl(consultant.photo_url)!}
                  alt={consultant?.full_name || 'User'}
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#40E0D0] flex-shrink-0"
                  style={{ objectPosition: `${photoPosition.x}% ${photoPosition.y}%` }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fb = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fb) fb.style.display = 'flex';
                  }}
                />
              )}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${
                  ['bg-blue-500', 'bg-green-500', 'bg-teal-500', 'bg-pink-500', 'bg-cyan-500'][
                    displayName?.charCodeAt(0) % 5 || 0
                  ]
                }`}
                style={{ display: consultant?.photo_url && getPhotoUrl(consultant.photo_url) ? 'none' : 'flex' }}
              >
                {displayName?.charAt(0) || '?'}
              </div>
            </>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <h3 className={`font-semibold ${textPrimary}`}>
                {displayName}
              </h3>

              {(user && !isOwnPost || readOnly) && !isAdminPost && (
                <>
                  <div className="relative">
                    <button
                      onClick={readOnly ? onReadOnlyAction : handleConnect}
                      disabled={!readOnly && connectionLoading}
                      onMouseEnter={() => setShowConnectionTooltip(true)}
                      onMouseLeave={() => setShowConnectionTooltip(false)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border-2 shadow-sm transition-all duration-200 select-none ${
                        readOnly
                          ? 'bg-[#40E0D0]/10 border-[#40E0D0] text-[#1a9e90] hover:bg-[#40E0D0] hover:text-white'
                          : connectionStatus === 'accepted'
                          ? 'bg-[#e6faf8] border-[#40E0D0] text-[#1a9e90] hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                          : connectionStatus === 'pending_sent'
                          ? 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-red-50 hover:border-red-300 hover:text-red-500'
                          : connectionStatus === 'pending_received'
                          ? 'bg-[#40E0D0] border-[#40E0D0] text-white hover:bg-[#2ecfc0] hover:border-[#2ecfc0]'
                          : 'bg-[#40E0D0]/10 border-[#40E0D0] text-[#1a9e90] hover:bg-[#40E0D0] hover:text-white'
                      }`}
                    >
                      {!readOnly && connectionLoading
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : readOnly || connectionStatus === 'none'
                        ? <UserPlus className="w-3.5 h-3.5" />
                        : connectionStatus === 'accepted'
                        ? <UserCheck className="w-3.5 h-3.5" />
                        : connectionStatus === 'pending_sent'
                        ? <UserX className="w-3.5 h-3.5" />
                        : <UserCheck className="w-3.5 h-3.5" />
                      }
                      <span>{readOnly ? 'Connect' : getConnectionLabel()}</span>
                    </button>
                    {showConnectionTooltip && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-64 bg-gray-900 text-white text-xs rounded-xl px-3.5 py-2.5 z-20 shadow-xl pointer-events-none leading-relaxed">
                        {readOnly
                          ? 'Sign up as a Workday consultant or Workday professional to unlock requests and unlock private messaging'
                          : getConnectionTooltip()
                        }
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-gray-900" />
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <button
                      onClick={readOnly ? onReadOnlyAction : handleFollow}
                      disabled={!readOnly && followLoading}
                      onMouseEnter={() => setShowFollowTooltip(true)}
                      onMouseLeave={() => setShowFollowTooltip(false)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border-2 shadow-sm transition-all duration-200 select-none ${
                        readOnly
                          ? 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-[#40E0D0]/10 hover:border-[#40E0D0] hover:text-[#1a9e90]'
                          : isFollowing
                          ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                          : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-[#40E0D0]/10 hover:border-[#40E0D0] hover:text-[#1a9e90]'
                      }`}
                    >
                      {!readOnly && followLoading
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : !readOnly && isFollowing
                        ? <BellOff className="w-3.5 h-3.5" />
                        : <Bell className="w-3.5 h-3.5" />
                      }
                      <span>{!readOnly && isFollowing ? 'Following' : 'Follow'}</span>
                    </button>
                    {showFollowTooltip && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-64 bg-gray-900 text-white text-xs rounded-xl px-3.5 py-2.5 z-20 shadow-xl pointer-events-none leading-relaxed">
                        {readOnly
                          ? 'Sign up as a Workday consultant or Workday professional to follow this person and get notified when they post'
                          : isFollowing
                          ? 'Unfollow to stop receiving their public post updates in your feed'
                          : 'Follow to stay updated on their public posts — no approval needed'
                        }
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-gray-900" />
                      </div>
                    )}
                  </div>
                </>
              )}

              {(user && !isOwnPost) && isAdminPost && (
                <div className="relative">
                  <span
                    onMouseEnter={() => setShowConnectionTooltip(true)}
                    onMouseLeave={() => setShowConnectionTooltip(false)}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border-2 bg-[#40E0D0]/10 border-[#40E0D0]/60 text-[#1a9e90] cursor-default select-none"
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    <span>Official</span>
                  </span>
                  {showConnectionTooltip && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-64 bg-gray-900 text-white text-xs rounded-xl px-3.5 py-2.5 z-20 shadow-xl pointer-events-none leading-relaxed">
                      This is a verified official post from the Perplexity Team.
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-gray-900" />
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className={`text-sm ${textMuted}`}>
              {displayCompany} · {formatDate(post.created_at)}
            </p>
            {!isAdminPost && (
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-xs font-medium ${textMuted} flex items-center gap-1`}>
                  <UserCheck className="w-3 h-3" />
                  {connectionsCount} {connectionsCount === 1 ? 'connection' : 'connections'}
                </span>
                <span className={`text-xs ${textMuted}`}>·</span>
                <span className={`text-xs font-medium ${textMuted} flex items-center gap-1`}>
                  <Bell className="w-3 h-3" />
                  {followersCount} {followersCount === 1 ? 'follower' : 'followers'}
                </span>
              </div>
            )}
          </div>
        </div>

        {isOwnPost && (
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className={`p-2 rounded-full transition-colors ${hoverBg}`}
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
            {showMenu && (
              <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg z-10 border ${
                theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
              }`}>
                <button
                  onClick={() => { setIsEditing(true); setShowMenu(false); }}
                  className={`w-full flex items-center space-x-2 px-4 py-2 text-left transition-colors ${
                    theme === 'dark' ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <EditIcon className="w-4 h-4" />
                  <span>Edit Post</span>
                </button>
                <button
                  onClick={handleDelete}
                  className={`w-full flex items-center space-x-2 px-4 py-2 text-left transition-colors ${
                    theme === 'dark' ? 'hover:bg-gray-600 text-red-400' : 'hover:bg-gray-100 text-red-600'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Post</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-4">
        {isEditing ? (
          <div className="space-y-4">
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] transition-all resize-none ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
              }`}
              rows={4}
            />
            <div className={`p-3 rounded-lg border-2 ${
              theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <p className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Post Visibility
              </p>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setEditedVisibility('community')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    editedVisibility === 'community'
                      ? 'bg-[#40E0D0] text-white'
                      : theme === 'dark'
                      ? 'bg-gray-800 text-gray-400 hover:bg-gray-600'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span className="font-medium">Community Only</span>
                </button>
                <button
                  type="button"
                  onClick={() => setEditedVisibility('public')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    editedVisibility === 'public'
                      ? 'bg-[#40E0D0] text-white'
                      : theme === 'dark'
                      ? 'bg-gray-800 text-gray-400 hover:bg-gray-600'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  <span className="font-medium">Public</span>
                </button>
              </div>
              <p className={`text-xs mt-2 ${textMuted}`}>
                {editedVisibility === 'community'
                  ? 'Only consultants who signed up can see this post'
                  : 'Everyone, including non-members, can see this post'}
              </p>
            </div>
            {/* Image repositioning in edit mode */}
            {(mediaUrls.length > 0 || pendingNewMedia) && post.media_type === 'image' && (
              <div className={`rounded-lg border-2 p-4 space-y-3 ${theme === 'dark' ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                    Post Image
                  </p>
                  <label className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white border border-gray-300 hover:bg-gray-100 text-gray-700'}`}>
                    <Upload className="w-3.5 h-3.5" />
                    Change Photo
                    <input
                      ref={editImageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleEditNewMediaSelect}
                    />
                  </label>
                </div>

                {(pendingNewMedia ? [pendingNewMedia.preview] : mediaUrls).map((url: string, idx: number) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {adjustingIndex === idx ? 'Drag the image to reposition' : 'Click "Adjust" to reposition the crop'}
                      </p>
                      {adjustingIndex !== idx && (
                        <button
                          type="button"
                          onClick={() => setAdjustingIndex(idx)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-[#40E0D0] hover:text-[#35c5b5] transition-colors"
                        >
                          <Move className="w-3.5 h-3.5" />
                          Adjust
                        </button>
                      )}
                      {adjustingIndex === idx && (
                        <button
                          type="button"
                          onClick={() => setAdjustingIndex(null)}
                          className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          Done
                        </button>
                      )}
                    </div>
                    <div
                      ref={el => { mediaDragRefs.current[idx] = el; }}
                      className={`relative rounded-lg overflow-hidden ${adjustingIndex === idx ? 'cursor-grab active:cursor-grabbing ring-2 ring-[#40E0D0]' : 'cursor-default'}`}
                      style={{ maxHeight: '260px', height: '260px' }}
                      onMouseDown={adjustingIndex === idx ? (e) => handleMediaDragStart(e, idx) : undefined}
                      onTouchStart={adjustingIndex === idx ? (e) => handleMediaDragStart(e, idx) : undefined}
                    >
                      <img
                        src={url}
                        alt="Post media"
                        className="w-full h-full object-cover select-none pointer-events-none"
                        style={{ objectPosition: `${mediaPositions[idx]?.x ?? 50}% ${mediaPositions[idx]?.y ?? 50}%` }}
                        draggable={false}
                      />
                      {adjustingIndex === idx && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="bg-black/30 rounded-full p-3">
                            <Move className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    {pendingNewMedia && (
                      <p className="text-xs text-[#40E0D0] mt-1">New photo selected — save to apply.</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center space-x-3">
              <button
                onClick={handleEditSave}
                disabled={isSavingEdit || !editedContent.trim()}
                className="flex items-center space-x-2 px-6 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                {isSavingEdit ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></>
                ) : (
                  <span>Save Changes</span>
                )}
              </button>
              <button
                onClick={handleEditCancel}
                disabled={isSavingEdit}
                className={`px-6 py-2 font-semibold rounded-lg transition-colors ${
                  theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className={`text-base whitespace-pre-wrap ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
              {post.content}
            </p>
            {post.hashtags && post.hashtags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {post.hashtags.map((tag: string, index: number) => (
                  <span key={index} className="text-[#40E0D0] hover:underline cursor-pointer font-medium">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
            {post.visibility && (
              <div className="flex items-center space-x-2 mt-3">
                {post.visibility === 'public' ? (
                  <><Globe className="w-4 h-4 text-gray-400" /><span className={`text-xs ${textMuted}`}>Public post</span></>
                ) : (
                  <><Users className="w-4 h-4 text-gray-400" /><span className={`text-xs ${textMuted}`}>Community only</span></>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {mediaUrls.length > 0 && (
        <div className={`mb-4 ${mediaUrls.length === 1 ? '' : 'grid grid-cols-2 gap-2'}`}>
          {mediaUrls.map((url: string, index: number) => {
            const savedPositions = Array.isArray(post.media_positions) ? post.media_positions : [];
            const pos = savedPositions[index] ?? mediaPositions[index] ?? { x: 50, y: 50 };
            return (
              <div key={index} className="rounded-lg overflow-hidden">
                {post.media_type === 'image' ? (
                  <img
                    src={url}
                    alt={`Post media ${index + 1}`}
                    className="w-full object-cover max-h-96"
                    style={{ objectPosition: `${pos.x}% ${pos.y}%` }}
                  />
                ) : post.media_type === 'video' ? (
                  <video src={url} controls className="w-full h-auto max-h-96" />
                ) : post.media_type === 'audio' ? (
                  <audio src={url} controls className="w-full" />
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <div className={`flex items-center justify-between py-3 border-t border-b ${borderColor}`}>
        <div className="flex items-center space-x-1 text-sm">
          <span className={textMuted}>{likesCount} {likesCount === 1 ? 'like' : 'likes'}</span>
        </div>
        <div className="flex items-center space-x-4 text-sm">
          <button
            onClick={toggleComments}
            className={`${textMuted} hover:underline`}
          >
            {commentsCount} {commentsCount === 1 ? 'comment' : 'comments'}
          </button>
          {sharesCount > 0 && (
            <span className={textMuted}>{sharesCount} {sharesCount === 1 ? 'share' : 'shares'}</span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-around pt-3">
        <button
          onClick={readOnly ? onReadOnlyAction : handleLike}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            isLiked ? 'text-[#40E0D0]' : `${textMuted} ${hoverBg}`
          }`}
        >
          <ThumbsUp className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
          <span className="font-medium">Like</span>
        </button>

        <button
          onClick={toggleComments}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${textMuted} ${hoverBg}`}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium">Comment</span>
        </button>

        <div className="relative" ref={shareMenuRef}>
          <button
            onClick={handleOpenShareMenu}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${textMuted} ${hoverBg}`}
          >
            {linkCopied ? (
              <><Check className="w-5 h-5 text-green-500" /><span className="font-medium text-green-500">Copied!</span></>
            ) : (
              <><Share2 className="w-5 h-5" /><span className="font-medium">Share</span></>
            )}
          </button>

          {showShareMenu && !readOnly && (
            <div className={`absolute bottom-full mb-2 right-0 w-80 rounded-xl shadow-xl border z-30 ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className={`flex items-center justify-between px-4 py-3 border-b ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
              }`}>
                <span className={`font-semibold text-sm ${textPrimary}`}>Share Post</span>
                <button onClick={() => setShowShareMenu(false)} className={`${textMuted} hover:text-gray-900`}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={handleCopyLink}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors border-b ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 border-gray-700 text-gray-300'
                    : 'hover:bg-gray-50 border-gray-100 text-gray-700'
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <LinkIcon className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Copy Link</p>
                  <p className={`text-xs ${textMuted}`}>
                    {post.visibility === 'public' ? 'Share with anyone' : 'Share with members'}
                  </p>
                </div>
              </button>

              <div className="px-4 py-2">
                <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${textMuted}`}>
                  Share with consultant
                </p>
                <input
                  type="text"
                  placeholder="Search consultants..."
                  value={shareSearch}
                  onChange={(e) => setShareSearch(e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#40E0D0] mb-2 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                  }`}
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredShareConsultants.length === 0 ? (
                    <p className={`text-sm text-center py-3 ${textMuted}`}>No consultants found</p>
                  ) : (
                    filteredShareConsultants.map(c => {
                      const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                      const sent = sharedTo.has(c.id);
                      return (
                        <div key={c.id} className={`flex items-center justify-between px-2 py-1.5 rounded-lg ${hoverBg}`}>
                          <div className="flex items-center space-x-2">
                            {c.photo_url && getPhotoUrl(c.photo_url) ? (
                              <>
                                <img
                                  src={getPhotoUrl(c.photo_url)!}
                                  alt={name}
                                  className="w-7 h-7 rounded-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fb = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fb) fb.style.display = 'flex';
                                  }}
                                />
                                <div
                                  className={`w-7 h-7 rounded-full items-center justify-center text-white text-xs font-bold ${
                                    ['bg-blue-500', 'bg-green-500', 'bg-teal-500'][name.charCodeAt(0) % 3]
                                  }`}
                                  style={{ display: 'none' }}
                                >
                                  {name.charAt(0)}
                                </div>
                              </>
                            ) : (
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                ['bg-blue-500', 'bg-green-500', 'bg-teal-500'][name.charCodeAt(0) % 3]
                              }`}>
                                {name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <p className={`text-sm font-medium ${textPrimary}`}>{name}</p>
                              {c.company && <p className={`text-xs ${textMuted}`}>{c.company}</p>}
                            </div>
                          </div>
                          <button
                            onClick={() => handleShareToConsultant(c.id)}
                            disabled={sent}
                            className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                              sent
                                ? 'bg-green-100 text-green-700 cursor-default'
                                : 'bg-[#40E0D0] text-white hover:bg-[#35c5b5]'
                            }`}
                          >
                            {sent ? 'Sent' : 'Send'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showComments && (
        <div className={`mt-4 pt-4 border-t ${borderColor}`}>
          {!readOnly && (
            <form onSubmit={handleCommentSubmit} className="mb-4">
              <div className="flex items-start space-x-3">
                {currentUserPhotoUrl ? (
                  <>
                    <img
                      src={currentUserPhotoUrl}
                      alt="Your profile"
                      className="w-10 h-10 rounded-full object-cover border-2 border-[#40E0D0]"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      className={`w-10 h-10 rounded-full items-center justify-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'}`}
                      style={{ display: 'none' }}
                    >
                      <User className={`w-6 h-6 ${textMuted}`} />
                    </div>
                  </>
                ) : (
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'
                  }`}>
                    <User className={`w-6 h-6 ${textMuted}`} />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    className={`w-full px-4 py-2 rounded-full border focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!newComment.trim() || isSubmittingComment}
                  className="p-2 bg-[#40E0D0] hover:bg-[#35c5b5] disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          )}

          <div className="space-y-3">
            {comments.map((comment) => (
              <div key={comment.id} className="flex items-start space-x-3">
                {comment.consultant?.photo_url && getPhotoUrl(comment.consultant.photo_url) ? (
                  <>
                    <img
                      src={getPhotoUrl(comment.consultant.photo_url)!}
                      alt={comment.consultant?.full_name || 'User'}
                      className="w-8 h-8 rounded-full object-cover border-2 border-[#40E0D0] flex-shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      className={`w-8 h-8 rounded-full items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
                        ['bg-blue-500', 'bg-green-500', 'bg-teal-500', 'bg-pink-500'][
                          comment.consultant?.full_name?.charCodeAt(0) % 4 || 0
                        ]
                      }`}
                      style={{ display: 'none' }}
                    >
                      {comment.consultant?.full_name?.charAt(0) || '?'}
                    </div>
                  </>
                ) : (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
                    ['bg-blue-500', 'bg-green-500', 'bg-teal-500', 'bg-pink-500'][
                      comment.consultant?.full_name?.charCodeAt(0) % 4 || 0
                    ]
                  }`}>
                    {comment.consultant?.full_name?.charAt(0) || '?'}
                  </div>
                )}
                <div className="flex-1">
                  {editingCommentId === comment.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editingCommentContent}
                        onChange={(e) => setEditingCommentContent(e.target.value)}
                        className={`w-full px-3 py-2 rounded-xl border-2 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] text-sm ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        autoFocus
                      />
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleSaveCommentEdit(comment.id)}
                          disabled={isSavingCommentEdit || !editingCommentContent.trim()}
                          className="text-xs px-3 py-1 bg-[#40E0D0] text-white rounded-full font-semibold hover:bg-[#35c5b5] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSavingCommentEdit ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingCommentId(null)}
                          className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                            theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={`px-4 py-2 rounded-2xl ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <p className={`font-semibold text-sm ${textPrimary}`}>
                        {comment.consultant?.full_name || 'Unknown'}
                      </p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>
                        {comment.content}
                      </p>
                    </div>
                  )}
                  <div className="flex items-center space-x-4 mt-1 px-4">
                    <span className={`text-xs ${textMuted}`}>{formatDate(comment.created_at)}</span>
                    {!readOnly && user && comment.consultant_id === user.id && editingCommentId !== comment.id && (
                      <>
                        <button
                          onClick={() => handleEditComment(comment)}
                          className="text-xs text-[#40E0D0] hover:underline font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-xs text-red-500 hover:underline font-medium"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
